import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IonApp,
  IonContent,
  IonFooter,
  IonMenu,
  IonRouterOutlet,
  IonSplitPane,
  ToastController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConnectivityNoticeService, OfflineOverlayComponent, ScreensizeService } from '@okcargo-apps/layout-ionic';
import { AccountService, SessionLifecycleService } from '@okcargo-apps/shared-application-ionic';
import { CheckVersionService, ConnectivityService, PwaUpdateService } from '@okcargo-apps/shared-infrastructure';
import { PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';
import { RefreshDataService } from './common/application/refresh-data.service';
import { MenuFooterComponent } from './layout/components/menu-footer/menu-footer.component';
import { MenuHeaderComponent } from './layout/components/menu-header/menu-header.component';
import { MenuMainComponent } from './layout/components/menu-main/menu-main.component';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [
    CommonModule,
    IonApp,
    IonRouterOutlet,
    IonFooter,
    IonMenu,
    IonSplitPane,
    IonContent,
    TranslateModule,
    MenuHeaderComponent,
    MenuMainComponent,
    MenuFooterComponent,
    OfflineOverlayComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ScreensizeService],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly checkVersionService = inject(CheckVersionService);
  private readonly pwaUpdateService = inject(PwaUpdateService);
  private readonly accountService = inject(AccountService);
  private readonly platformService = inject(PlatformService);
  private readonly connectivityService = inject(ConnectivityService);
  private readonly connectivityNoticeService = inject(ConnectivityNoticeService);

  /**
   * While the blocking layer is visible (browser without connectivity) the app content is inert, so
   * it cannot be reached with the keyboard either. The layer and the toast are rendered outside it.
   */
  protected readonly blocksInteraction = this.connectivityNoticeService.isOverlayVisible;
  private readonly toastCtrl = inject(ToastController);
  private readonly screensizeService = inject(ScreensizeService);
  private readonly refreshDataService = inject(RefreshDataService);
  private readonly sessionLifecycle = inject(SessionLifecycleService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  public readonly isUpdateRequired = this.checkVersionService.isUpdateRequired;

  /**
   * Whether the version check blocks the app. Without connectivity the check is skipped, so the app
   * renders instead of waiting for a result that cannot arrive; with connectivity the previous
   * behaviour is kept (placeholder while checking and the mandatory update screen when required).
   */
  protected readonly isVersionCheckBlocking = computed(
    () => this.checkVersionService.isUpdateRequired() === undefined && this.connectivityService.isOnline(),
  );

  protected isAuthenticated = this.accountService.isAuthenticated;

  public menuPosition = computed(() => {
    return this.screensizeService.isDesktopView() ? 'start' : 'end';
  });

  constructor() {
    const browserLang = navigator.language || navigator.languages[0];
    const defaultLang = browserLang.startsWith('es') ? 'es' : 'en';
    this.translate.setFallbackLang(defaultLang);
    this.translate.use(defaultLang);

    effect(() => {
      const account = this.accountService.account();
      if (account && this.translate.getFallbackLang() !== account.lang) {
        this.translate.setFallbackLang(account.lang);
        this.translate.use(account.lang);
      }
    });

    effect(() => {
      if (this.isUpdateRequired()) {
        this.router.navigate(['/update-required'], { replaceUrl: true });
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.platformService.ready();
    // The connectivity status is started by the app bootstrap (provideConnectivityBootstrap), so it is
    // already known here.
    this.connectivityNoticeService.start();

    if (!this.platformService.isHybrid) {
      effect(
        () => {
          if (this.pwaUpdateService.isUpdateReady()) {
            this.handlePwaUpdate();
          }
        },
        { injector: this.injector },
      );
    }

    // docs-app does not refresh when the session is verified: its resources already react to the
    // account signal (see RefreshDataService).
    this.sessionLifecycle.start({
      refreshAll: () => this.refreshDataService.refreshAll(),
      refreshPendingData: () => this.refreshDataService.refreshPendingData(),
    });
  }

  ngOnDestroy(): void {
    this.sessionLifecycle.stop();
  }

  public reloadApp(): void {
    window.location.href = window.location.origin;
  }

  private async handlePwaUpdate(): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: this.translate.instant('general.update_version_available'),
      color: 'danger',
      position: 'bottom',
      buttons: [
        {
          text: this.translate.instant('general.update'),
          role: 'info',
          handler: () => {
            this.applyUpdate();
          },
        },
      ],
    });
    await toast.present();
  }

  private async applyUpdate(): Promise<void> {
    await this.pwaUpdateService.activateUpdate();
    this.reloadApp();
  }
}
